import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import { companyInfo } from '../utils/dataInfo';

// Helper function to get status name
const getStatusName = (status) => {
  return status === 1 ? 'Active' : 'Inactive';
};

// Helper function to get gender name
const getGenderName = (gender) => {
  if (!gender) return 'Not Specified';
  return gender.charAt(0).toUpperCase() + gender.slice(1);
};

// Helper function to get selection name by ID
const getSelectionNameById = (id, dataArray) => {
  if (!dataArray || !Array.isArray(dataArray)) return 'Unknown';
  const item = dataArray.find(item => parseInt(item.id) === parseInt(id));
  return item ? item.name : 'Unknown';
};

// Helper function to get duty type name
const getDutyTypeName = (dutyType) => {
  const dutyTypes = {
    'full_time': 'Full Time',
    'part_time': 'Part Time',
    'contract': 'Contract',
    'internship': 'Internship'
  };
  return dutyTypes[dutyType] || dutyType || 'Not Specified';
};

// Helper function to get pay frequency name
const getPayFrequencyName = (payFrequency) => {
  const payFrequencies = {
    'monthly': 'Monthly',
    'biweekly': 'Bi-Weekly',
    'weekly': 'Weekly',
    'daily': 'Daily'
  };
  return payFrequencies[payFrequency] || payFrequency || 'Not Specified';
};

// Helper function to split text into multiple lines
const splitTextIntoLines = (doc, text, maxWidth) => {
  const words = text.split(' ');
  const lines = [];
  let currentLine = words[0];

  for (let i = 1; i < words.length; i++) {
    const word = words[i];
    const width = doc.getTextWidth(currentLine + ' ' + word);
    if (width < maxWidth) {
      currentLine += ' ' + word;
    } else {
      lines.push(currentLine);
      currentLine = word;
    }
  }
  lines.push(currentLine);
  return lines;
};

// Manual table drawing function for PDF with optimized columns
const drawEmployeesTable = (doc, data, startY) => {
  const pageWidth = doc.internal.pageSize.width;
  const margin = 14;
  const rowHeight = 10;

  // Column widths for employees - optimized for the requested columns
  // #, Employee Name, Email, Contact, Position, Department
  const colWidths = [10, 50, 45, 30, 35, 35];

  // Check if we need a new page
  let currentY = startY;
  const neededHeight = (data.length + 1) * rowHeight;
  if (currentY + neededHeight > doc.internal.pageSize.height - 20) {
    doc.addPage();
    currentY = margin;
  }

  // Draw table header
  doc.setFillColor(255, 255, 255);
  doc.setTextColor(25, 118, 210);
  doc.setFont(undefined, 'bold');

  const headers = ['#', 'Name','Contact', 'Position', 'Department'];
  let xPos = margin;

  // Draw header background
  doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');

  // Draw header text
  headers.forEach((header, index) => {
    const cellX = xPos + 2;
    doc.text(header, cellX, currentY + 5);
    xPos += colWidths[index];
  });

  // Draw header underline
  doc.setDrawColor(25, 118, 210);
  doc.setLineWidth(0.5);
  doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);

  currentY += rowHeight;

  // Draw table rows
  doc.setFont(undefined, 'normal');
  doc.setTextColor(0, 0, 0);

  data.forEach((row, rowIndex) => {
    // Calculate needed height for this row
    let maxLines = 1;
    const cellLines = row.map((cell, cellIndex) => {
      const text = String(cell);
      const maxWidth = colWidths[cellIndex] - 4;
      const lines = splitTextIntoLines(doc, text, maxWidth);
      maxLines = Math.max(maxLines, lines.length);
      return lines;
    });

    const actualRowHeight = rowHeight * maxLines;

    // Check for page break
    if (currentY + actualRowHeight > doc.internal.pageSize.height - 20) {
      doc.addPage();
      currentY = margin;
      
      // Redraw header on new page
      doc.setFillColor(255, 255, 255);
      doc.setTextColor(25, 118, 210);
      doc.setFont(undefined, 'bold');
      
      // Draw header background
      doc.rect(margin, currentY, pageWidth - (2 * margin), rowHeight, 'F');
      
      xPos = margin;
      headers.forEach((header, index) => {
        doc.text(header, xPos + 2, currentY + 5);
        xPos += colWidths[index];
      });
      
      // Draw header underline
      doc.setDrawColor(25, 118, 210);
      doc.line(margin, currentY + rowHeight, pageWidth - margin, currentY + rowHeight);
      
      currentY += rowHeight;
      doc.setFont(undefined, 'normal');
      doc.setTextColor(0, 0, 0);
    }
    
    // Alternate row colors
    if (rowIndex % 2 === 0) {
      doc.setFillColor(248, 248, 248);
    } else {
      doc.setFillColor(255, 255, 255);
    }
    
    // Draw row background
    doc.rect(margin, currentY, pageWidth - (2 * margin), actualRowHeight, 'F');
    
    // Draw row text with multi-line support
    cellLines.forEach((lines, cellIndex) => {
      xPos = margin + colWidths.slice(0, cellIndex).reduce((sum, width) => sum + width, 0);
      
      lines.forEach((line, lineIndex) => {
        const lineY = currentY + 5 + (lineIndex * rowHeight);
        doc.text(line, xPos + 2, lineY);
      });
    });
    
    currentY += actualRowHeight;
  });

  return currentY;
};

// Excel export for Employees with all attributes
export const exportEmployeesToExcel = (employees, selectionData = {}) => {
  try {
    const { departmentNamesData = [], positionNamesData = [], divisionNamesData = [] } = selectionData;
    
    // Prepare data for Excel with all attributes
    const excelData = employees.map((employee, index) => ({
      'No.': index + 1,
      'ID': employee.id,
      'Employee Name': employee.employee_name,
      'Email': employee.email,
      'Contact': employee.contact,
      'Gender': getGenderName(employee.gender),
      'Position': getSelectionNameById(employee.position_id, positionNamesData),
      'Department': getSelectionNameById(employee.department_id, departmentNamesData),
      'Division': getSelectionNameById(employee.division_id, divisionNamesData),
      'Duty Type': getDutyTypeName(employee.duty_type),
      'Pay Frequency': getPayFrequencyName(employee.pay_frequency),
      'Start Date': employee.start_date ? new Date(employee.start_date).toLocaleDateString() : '',
      'Status': getStatusName(employee.status),
      'Additional Info': employee.additional_info || '',
      'Created Date': new Date(employee.created_at).toLocaleDateString(),
      'Updated Date': new Date(employee.updated_at).toLocaleDateString()
    }));

    // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Auto-size columns
    const maxWidth = excelData.reduce((widths, row) => {
      Object.keys(row).forEach((key, idx) => {
        const length = String(row[key]).length;
        if (!widths[idx] || length > widths[idx]) {
          widths[idx] = length;
        }
      });
      return widths;
    }, []);
    
    worksheet['!cols'] = maxWidth.map(width => ({ width: Math.min(width + 2, 50) }));

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Employees');

    // Generate Excel file and download
    const fileName = `employees_export_${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(workbook, fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Employees Excel export failed:', error);
    throw new Error('Failed to export to Excel: ' + error.message);
  }
};

// Enhanced PDF export for Employees with selected columns
export const exportEmployeesToPDFEnhanced = (employees, selectionData = {}, filters = {}) => {
  try {
    const { departmentNamesData = [], positionNamesData = [] } = selectionData;
    
    const doc = new jsPDF();
    
    // Set document properties
    doc.setProperties({
      title: 'Employees Detailed Report',
      subject: 'Employees Export',
      author: companyInfo.name,
      keywords: 'employees, export, report',
      creator: companyInfo.name
    });
    
    // Title section
    doc.setFontSize(20);
    doc.setTextColor(25, 118, 210);
    doc.text('EMPLOYEES REPORT', 105, 25, { align: 'center' });
    
    // Generation details
    doc.setFontSize(10);
    doc.setTextColor(80, 80, 80);
    let currentY = 35;
    doc.text(`Report Generated: ${new Date().toLocaleString()}`, 14, currentY);
    currentY += 6;
    doc.text(`Total Employees: ${employees.length}`, 14, currentY);
    currentY += 6;
    
    // Filters applied
    if (filters.employee_name || filters.dateFrom || filters.dateTo || filters.status) {
      doc.text('Applied Filters:', 14, currentY);
      currentY += 6;
      
      if (filters.employee_name) {
        doc.text(`• Employee Name: ${filters.employee_name}`, 20, currentY);
        currentY += 6;
      }
      if (filters.status) {
        doc.text(`• Status: ${getStatusName(filters.status)}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateFrom) {
        doc.text(`• From date: ${new Date(filters.dateFrom).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
      }
      if (filters.dateTo) {
        doc.text(`• To date: ${new Date(filters.dateTo).toLocaleDateString()}`, 20, currentY);
        currentY += 6;
      }
      currentY += 6;
    }
    
    // Prepare table data with selected columns only
    const tableData = employees.map((employee, index) => [
      (index + 1).toString(),
      employee.employee_name,
      employee.contact,
      getSelectionNameById(employee.position_id, positionNamesData),
      getSelectionNameById(employee.department_id, departmentNamesData)
    ]);
    
    // Draw the table
    drawEmployeesTable(doc, tableData, currentY + 5);
    
    const fileName = `employees_report_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Employees PDF export failed:', error);
    throw new Error('Failed to export to PDF: ' + error.message);
  }
};

// CSV export for Employees with all attributes
export const exportEmployeesToCSV = (employees, selectionData = {}) => {
  try {
    const { departmentNamesData = [], positionNamesData = [], divisionNamesData = [] } = selectionData;
    
    // CSV headers with all attributes
    const headers = [
      'ID',
      'Employee Name',
      'Email',
      'Contact',
      'Gender',
      'Position',
      'Department',
      'Division',
      'Duty Type',
      'Pay Frequency',
      'Start Date',
      'Status',
      'Additional Info',
      'Created Date',
      'Updated Date'
    ];
    
    // Prepare CSV data with all attributes
    const csvData = employees.map(employee => [
      employee.id,
      `"${employee.employee_name.replace(/"/g, '""')}"`,
      `"${employee.email.replace(/"/g, '""')}"`,
      `"${employee.contact.replace(/"/g, '""')}"`,
      `"${getGenderName(employee.gender).replace(/"/g, '""')}"`,
      `"${getSelectionNameById(employee.position_id, positionNamesData).replace(/"/g, '""')}"`,
      `"${getSelectionNameById(employee.department_id, departmentNamesData).replace(/"/g, '""')}"`,
      `"${getSelectionNameById(employee.division_id, divisionNamesData).replace(/"/g, '""')}"`,
      `"${getDutyTypeName(employee.duty_type).replace(/"/g, '""')}"`,
      `"${getPayFrequencyName(employee.pay_frequency).replace(/"/g, '""')}"`,
      `"${employee.start_date ? new Date(employee.start_date).toLocaleDateString() : ''}"`,
      `"${getStatusName(employee.status).replace(/"/g, '""')}"`,
      `"${(employee.additional_info || '').replace(/"/g, '""')}"`,
      `"${new Date(employee.created_at).toLocaleDateString()}"`,
      `"${new Date(employee.updated_at).toLocaleDateString()}"`
    ]);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    // Create and download the file
    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `employees_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    return { success: true };
  } catch (error) {
    console.error('Employees CSV export failed:', error);
    throw new Error('Failed to export to CSV: ' + error.message);
  }
};